import React, { useEffect, useMemo } from "react";
import { Button, Modal } from "react-bootstrap";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import { GripVertical } from "react-bootstrap-icons";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/store";
import { ADMIN_LANG } from "../../../settings";
import AdminLoader from "../AdminLoader";
import { getListItems } from "../../../app/slices/adminList";
import {
    closeGameOrderModal,
    getGameOrder,
    setGameOrder,
    updateGameOrder
} from "../../../app/slices/modals/gameOrderModal";

const GameOrderModal = () => {
    const dispatch = useAppDispatch();
    const { isOpen, loading, games } = useAppSelector((state) => state.gameOrderModal);
    const [searchParams] = useSearchParams();

    useEffect(() => {
        if (isOpen) {
            dispatch(getGameOrder());
        }
    }, [dispatch, isOpen]);

    const listParams = useMemo(() => {
        const params: Record<string, string> = {};
        searchParams.forEach((value, key) => {
            params[key] = value;
        });
        return params;
    }, [searchParams]);

    const handleClose = () => {
        dispatch(closeGameOrderModal());
    };

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) return;
        const reorderedGames = [...games];
        const [removed] = reorderedGames.splice(result.source.index, 1);
        reorderedGames.splice(result.destination.index, 0, removed);
        dispatch(setGameOrder(reorderedGames));
        dispatch(updateGameOrder({ games: reorderedGames }))
            .unwrap()
            .then(() => {
                dispatch(getListItems({ listType: 'games', ...listParams }));
            })
            .catch(() => {});
    };

    return (
        <Modal size='xl' show={isOpen} onHide={handleClose} className='admin-modal'>
            {loading && <AdminLoader />}
            <Modal.Header closeButton>
                <Modal.Title>{ADMIN_LANG.edit_order}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="game-order">
                        {(provided, snapshot) => (
                            <div
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                                className={`section-order-container${snapshot.isDraggingOver ? ' dragging-over' : ''}`}
                            >
                                {games.map((game, index) => (
                                    <Draggable key={game.key} draggableId={game.key} index={index}>
                                        {(provided, snapshot) => (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                className={`section-order-wrapper${snapshot.isDragging ? ' dragging' : ''}`}
                                            >
                                                <span className='section-order-text'>
                                                    {game.name}
                                                </span>
                                                <GripVertical className='utility-icon drag-icon' />
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </Modal.Body>
            <Modal.Footer>
                <Button variant='outline-primary' onClick={handleClose}>
                    {ADMIN_LANG.close}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default GameOrderModal;
